import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'

// Types
export interface PropertyMappingRule {
  uid: string
  code?: string
  document_type_uid: string
  asset_type_uid: string
  enabled: boolean
  /** subject_only: never written for a document filed below the rule's asset. */
  target?: MappingRuleTarget
  created_at: string
  updated_at: string
  document_type?: {
    uid: string
    code: string
    name: Record<string, string>
  }
  asset_type?: {
    uid: string
    code: string
    name: string | Record<string, string>
    emoji?: string
  }
  entries?: PropertyMappingRuleEntry[]
}

export type MappingRuleTarget = 'subject_or_ancestor' | 'subject_only'

export interface PropertyMappingRuleEntry {
  uid: string
  source_property_uid: string
  target_property_uid?: string
  role: 'value' | 'start_date' | 'end_date'
  timelined: boolean
  position: number
  /** The entry applies only when the document's value of this property equals when_equals. */
  when_property_code?: string | null
  when_equals?: unknown
  created_at: string
  updated_at: string
  source_property?: {
    uid: string
    code: string
    name: Record<string, string>
  }
  target_property?: {
    uid: string
    code: string
    name: Record<string, string>
  }
}

interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
}

// Query keys
export const pmrKeys = {
  rules: (org: string) => ['property-mapping-rules', org] as const,
  rule: (org: string, uid: string) => ['property-mapping-rule', org, uid] as const,
  entries: (org: string, ruleUid: string) => ['property-mapping-rule-entries', org, ruleUid] as const,
}

// Helper to get auth token
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw { status: res.status, ...body }
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// --- Rules hooks ---

interface ListRulesParams {
  document_type_uid?: string
  asset_type_uid?: string
  enabled?: string
  q?: string
}

export function usePropertyMappingRules(org: string, params?: ListRulesParams) {
  const queryString = new URLSearchParams()
  queryString.set('limit', '100')
  if (params?.document_type_uid) queryString.set('document_type_uid', params.document_type_uid)
  if (params?.asset_type_uid) queryString.set('asset_type_uid', params.asset_type_uid)
  if (params?.enabled) queryString.set('enabled', params.enabled)

  return useQuery({
    queryKey: [...pmrKeys.rules(org), params],
    queryFn: () => apiFetch<PaginatedResponse<PropertyMappingRule>>(
      `/api/v1/organizations/${org}/property-mapping-rules?${queryString}`
    ),
    enabled: !!org,
    select: (data) => ({ rules: data.data, total: data.total }),
  })
}

export function usePropertyMappingRule(org: string, uid: string) {
  return useQuery({
    queryKey: pmrKeys.rule(org, uid),
    queryFn: () => apiFetch<PropertyMappingRule>(
      `/api/v1/organizations/${org}/property-mapping-rules/${uid}`
    ),
    enabled: !!org && !!uid,
  })
}

export function useCreatePropertyMappingRule(org: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { code?: string; document_type_uid: string; asset_type_uid: string; enabled?: boolean }) =>
      apiFetch<PropertyMappingRule>(`/api/v1/organizations/${org}/property-mapping-rules`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pmrKeys.rules(org) })
    },
  })
}

export function useUpdatePropertyMappingRule(org: string, uid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { code?: string; enabled?: boolean; target?: MappingRuleTarget }) =>
      apiFetch<PropertyMappingRule>(`/api/v1/organizations/${org}/property-mapping-rules/${uid}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pmrKeys.rules(org) })
      queryClient.invalidateQueries({ queryKey: pmrKeys.rule(org, uid) })
    },
  })
}

export function useDeletePropertyMappingRule(org: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (uid: string) =>
      apiFetch<void>(`/api/v1/organizations/${org}/property-mapping-rules/${uid}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pmrKeys.rules(org) })
    },
  })
}

// --- Entries hooks ---

export function useCreatePropertyMappingRuleEntry(org: string, ruleUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { source_property_code: string; target_property_code?: string; role: string; timelined?: boolean; position?: number; when_property_code?: string; when_equals?: unknown }) =>
      apiFetch<PropertyMappingRuleEntry>(`/api/v1/organizations/${org}/property-mapping-rules/${ruleUid}/entries`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pmrKeys.rule(org, ruleUid) })
      queryClient.invalidateQueries({ queryKey: pmrKeys.entries(org, ruleUid) })
    },
  })
}

export function useUpdatePropertyMappingRuleEntry(org: string, ruleUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entryUid, ...body }: { entryUid: string; source_property_code?: string; target_property_code?: string; role?: string; timelined?: boolean; position?: number; when_property_code?: string; when_equals?: unknown }) =>
      apiFetch<PropertyMappingRuleEntry>(`/api/v1/organizations/${org}/property-mapping-rules/${ruleUid}/entries/${entryUid}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pmrKeys.rule(org, ruleUid) })
      queryClient.invalidateQueries({ queryKey: pmrKeys.entries(org, ruleUid) })
    },
  })
}

export function useDeletePropertyMappingRuleEntry(org: string, ruleUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entryUid: string) =>
      apiFetch<void>(`/api/v1/organizations/${org}/property-mapping-rules/${ruleUid}/entries/${entryUid}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pmrKeys.rule(org, ruleUid) })
      queryClient.invalidateQueries({ queryKey: pmrKeys.entries(org, ruleUid) })
    },
  })
}
